/**
 * Plan feature derivation shared by the plan surfaces.
 *
 * Returns data, never markup: the desktop comparison card renders these as a
 * single column with icons, the mobile registration list as a two-column grid,
 * and both must agree on which rows exist and how a limit reads.
 */

import { DEFAULT_VOICE_TRIAL_DURATION_HOURS } from '@/lib/billing/constants'
import { formatCredits as englishFormatCredits } from '@/lib/billing/utils/credits'
import { createTranslator, type useTranslations } from 'next-intl'
import english from '@/translations/en/plan-features.json'

const englishTranslator = createTranslator({locale: 'en', messages: english, namespace: 'PlanFeatures'})

export type PlanVoiceStatus = 'included' | 'trial' | 'none'

export interface PlanVoiceLabel {
  status: PlanVoiceStatus
  text: string
}

interface PlanVoiceFields {
  voice_included: boolean
  voice_trial_enabled: boolean
  voice_trial_duration_hours: number | null
}

export interface PlanCreditFields {
  price_monthly_cents: number
  initial_balance_microusd: number
  payment_to_balance_microusd: number
  payment_to_balance_count: number | null
}

export interface PlanCreditLifetimeFields {
  price_monthly_cents: number
  payment_to_balance_microusd: number
  plan_credits_expire: boolean
}

export interface PlanLimitRow {
  label: string
  value: number | null
  unit?: string
  /** The Shopify-stores row is the only one that carries the Insights trial hint. */
  shopifyStores?: boolean
}

interface PlanLimitFields {
  voice_included: boolean
  voice_trial_enabled: boolean
  voice_agents_limit: number
  campaigns_enabled?: boolean
  campaigns_limit?: number | null
  pbx_numbers_limit: number | null
  knowledge_bases_limit: number | null
  knowledge_bases_size_mb_limit: number | null
  users_limit: number | null
  crm_integrations_limit: number | null
  shopify_stores_limit: number | null
}


export const { getPlanVoiceLabel, getPlanCreditsText, getPlanCreditsLifetimeText, getPlanLimitRows, formatPlanLimitRow, getInsightsTrialText, getPlanBenefits } = getPlanFeatureFormatters()

export function getPlanFeatureFormatters(
  t: ReturnType<typeof useTranslations<'PlanFeatures'>> = englishTranslator,
  formatCredits: typeof englishFormatCredits = englishFormatCredits,
) {
  return { getPlanVoiceLabel, getPlanCreditsText, getPlanCreditsLifetimeText, getPlanLimitRows, formatPlanLimitRow, getInsightsTrialText, getPlanBenefits }

  function getPlanVoiceLabel(plan: PlanVoiceFields): PlanVoiceLabel {
    if (plan.voice_included) {
      return { status: 'included', text: t("voiceIncluded") }
    }
    if (plan.voice_trial_enabled) {
      const days = Math.round((plan.voice_trial_duration_hours ?? DEFAULT_VOICE_TRIAL_DURATION_HOURS) / 24)
      return { status: 'trial', text: t("voiceTrial", {days}) }
    }
    return { status: 'none', text: t("noVoice") }
  }

  function getPlanCreditsText(plan: PlanCreditFields): string | null {
    if (plan.price_monthly_cents === 0) {
      return plan.initial_balance_microusd > 0 ? t("oneTimeCredits", {credits: formatCredits(plan.initial_balance_microusd)}) : null
    }
    if (plan.payment_to_balance_microusd <= 0) return null
    const credits = formatCredits(plan.payment_to_balance_microusd)
    switch (plan.payment_to_balance_count) {
      case null:
        return t("monthlyCredits", {credits})
      case 1:
        return t("firstPaymentCredits", {credits})
      default:
        return t("limitedMonthlyCredits", {credits, count: plan.payment_to_balance_count})
    }
  }

  function getPlanCreditsLifetimeText(plan: PlanCreditLifetimeFields): string | null {
    if (plan.price_monthly_cents === 0 || plan.payment_to_balance_microusd <= 0) return null
    return plan.plan_credits_expire ? t("creditsExpire") : t("creditsRollOver")
  }

  function getPlanLimitRows(plan: PlanLimitFields): PlanLimitRow[] {
    return [
      // Text agents are not a plan limit; every plan includes them.
      { label: t("textAgents"), value: null },
      // Without voice the count is moot: next to "No Voice" it would read as an offer.
      ...(plan.voice_included || plan.voice_trial_enabled
        ? [{ label: t("voiceAgents"), value: plan.voice_agents_limit }]
        : []),
      ...(plan.campaigns_enabled === false
        ? []
        : [{ label: t("campaigns"), value: plan.campaigns_limit ?? null }]),
      { label: t("phoneNumbers"), value: plan.pbx_numbers_limit },
      { label: t("knowledgeBase"), value: plan.knowledge_bases_size_mb_limit, unit: 'MB' },
      { label: t("users"), value: plan.users_limit },
      { label: t("crmIntegrations"), value: plan.crm_integrations_limit },
      { label: t("shopifyStores"), value: plan.shopify_stores_limit, shopifyStores: true },
    ].filter(({ value }) => value !== 0)
  }

  function formatPlanLimitRow({ label, value, unit }: PlanLimitRow): string {
    if (value === null || value === undefined) return t("unlimited", {label})
    return unit ? t("limitWithUnit", {value, unit, label}) : t("limit", {value, label})
  }

  function getInsightsTrialText(hours: number | null | undefined): string | null {
    if (typeof hours !== 'number' || hours <= 0) return null
    return hours < 24 ? t("trialHours", {hours}) : t("trialDays", {days: Math.round(hours / 24)})
  }

  function getPlanBenefits(benefits: string[] | unknown): string[] {
    return Array.isArray(benefits) ? (benefits as string[]) : []
  }
}
